
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { ChevronRight } from 'lucide-react';
import { ClientProspect } from './ClientProspectCard';
import { useNavigate } from 'react-router-dom';

interface ClientProspectTableProps {
  data: ClientProspect[];
}

export function ClientProspectTable({ data }: ClientProspectTableProps) {
  const navigate = useNavigate();

  const getAvatarColor = (name: string) => {
    const colors = ['bg-info/10', 'bg-green-500', 'bg-primary-container', 'bg-orange-500', 'bg-pink-500', 'bg-teal-500'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const getStatusBadge = (status: string, type: string) => {
    if (type === 'client') {
      if (status === 'active') return <Badge variant="success">Active</Badge>;
      if (status === 'archived') return <Badge variant="secondary">Archived</Badge>;
    }
    
    switch (status) {
      case 'new': return <Badge variant="info">New</Badge>;
      case 'contacted': return <Badge variant="warning">Contacted</Badge>;
      case 'interested': return <Badge variant="primary">Interested</Badge>;
      case 'converted': return <Badge variant="success">Converted</Badge>;
      case 'rejected': return <Badge variant="danger">Rejected</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-muted border-b border-border text-on-surface-variant font-medium">
            <tr>
              <th className="px-4 py-3">Client/Prospect</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Industry</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Last Contact</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-on-surface-variant">
                  No records found.
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr 
                  key={item.id} 
                  className="hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/sales/clients-prospects/${item.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {item.logo ? (
                        <img src={item.logo} alt={item.name} className="w-8 h-8 rounded object-cover border border-border" />
                      ) : (
                        <div className={`w-8 h-8 rounded text-white flex items-center justify-center font-semibold text-xs shadow-sm ${getAvatarColor(item.name)}`}>
                          {item.name ? item.name.substring(0, 2).toUpperCase() : 'NA'}
                        </div>
                      )}
                      <span className="font-medium text-on-surface">{item.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={item.type === 'client' ? 'success' : 'primary'} className="text-xs">
                      {item.type === 'client' ? 'Client' : 'Prospect'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-on-surface-variant">{item.industry || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-on-surface">{item.phone || '-'}</span>
                      <span className="text-on-surface-variant text-xs">{item.email || '-'}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-on-surface-variant">{item.country || '-'}</td>
                  <td className="px-4 py-3">
                    {getStatusBadge(item.status, item.type)}
                  </td>
                  <td className="px-4 py-3 text-on-surface-variant">
                    {item.lastCommunicationDate ? new Date(item.lastCommunicationDate).toLocaleDateString('en-IN') : '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0 text-on-surface-subtle hover:text-on-surface"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
